import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import AddressManager, { MapPicker } from '../components/AddressManager';
import api from '../utils/api';
import { toast } from 'sonner';
import { CreditCard, Banknote, Tag, ChevronDown, ChevronUp, X, MapPin } from 'lucide-react';

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

// GST is flat 5% INCLUDED in the selling price
// For price Rs.100: GST = 100 * 5/105 = Rs.4.76
const calculateGST = (cartItems) => {
  let totalGST = 0;
  const breakdown = cartItems.map(item => {
    const price = item.product.price;
    const qty = item.quantity;
    const itemTotal = price * qty;
    const gst = Math.round(itemTotal * 5 / 105 * 100) / 100;
    totalGST += gst;
    return { name: item.product.name, itemTotal, rate: 5, gst };
  });
  return { totalGST: Math.round(totalGST * 100) / 100, breakdown };
};

const Checkout = () => {
  const { cart, cartLoading, getCartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [selectedAddress, setSelectedAddress] = useState(null);
  
  const [guestForm, setGuestForm] = useState({ name: '', email: '', phone: '', address: '', city: '', state: '', pincode: '' });
  const [showGuestMap, setShowGuestMap] = useState(false);
  const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [showOffers, setShowOffers] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);

  const [giftWrap, setGiftWrap] = useState(false);
  const [giftMessage, setGiftMessage] = useState('');
  const [isSurpriseGift, setIsSurpriseGift] = useState(false);
  const [billingAddress, setBillingAddress] = useState({ name: '', phone: '', address: '', city: '', state: '', pincode: '' });

  const GIFT_WRAP_COST = 100;

  const cartTotal = getCartTotal();
  const discount = appliedCoupon?.discount || 0;
  const giftWrapCharge = giftWrap ? GIFT_WRAP_COST : 0;
  const afterDiscount = cartTotal - discount + giftWrapCharge;
  const gstData = calculateGST(cart);
  // GST is INCLUDED in the selling price — total does NOT add GST on top
  const finalTotal = Math.round(afterDiscount * 100) / 100;

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await api.get('/coupons');
      setAvailableCoupons(response.data);
    } catch (error) { /* silent */ }
  };

  const handleApplyCoupon = async (code) => {
    const codeToApply = (code || couponCode).trim().toUpperCase();
    if (!codeToApply) return;
    setApplyingCoupon(true);
    try {
      const response = await api.post('/coupons/apply', { code: codeToApply, order_total: cartTotal });
      setAppliedCoupon(response.data);
      setCouponCode(codeToApply);
      setShowOffers(false);
      toast.success(`Coupon applied! You save ₹${response.data.discount.toLocaleString()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon');
      setAppliedCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => { setAppliedCoupon(null); setCouponCode(''); };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let shippingAddress;
    if (user && selectedAddress) {
      shippingAddress = {
        name: selectedAddress.name,
        phone: selectedAddress.phone,
        address_line1: selectedAddress.address_line1,
        address_line2: selectedAddress.address_line2 || '',
        address: selectedAddress.address_line1 + (selectedAddress.address_line2 ? ', ' + selectedAddress.address_line2 : ''),
        city: selectedAddress.city,
        state: selectedAddress.state,
        pincode: selectedAddress.pincode,
        lat: selectedAddress.lat || null,
        lng: selectedAddress.lng || null,
      };
    } else if (!user) {
      const emailRe = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const phoneRe = /^[6-9]\d{9}$/;
      const pincodeRe = /^\d{6}$/;
      if (!guestForm.name.trim() || guestForm.name.trim().length < 2) { toast.error('Please enter a valid name'); return; }
      if (!emailRe.test(guestForm.email.trim())) { toast.error('Please enter a valid email'); return; }
      if (guestForm.phone && !phoneRe.test(guestForm.phone.trim())) { toast.error('Please enter a valid 10-digit phone number'); return; }
      if (!guestForm.address.trim()) { toast.error('Please enter your address'); return; }
      if (!guestForm.city.trim()) { toast.error('Please enter your city'); return; }
      if (!guestForm.state.trim()) { toast.error('Please enter your state'); return; }
      if (!pincodeRe.test(guestForm.pincode.trim())) { toast.error('Please enter a valid 6-digit pincode'); return; }
      shippingAddress = { address: guestForm.address, city: guestForm.city, state: guestForm.state, pincode: guestForm.pincode };
    } else {
      toast.error('Please select or add a delivery address');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        user_id: user?.user_id || null,
        guest_email: !user ? guestForm.email : null,
        guest_name: !user ? guestForm.name : null,
        guest_phone: !user ? guestForm.phone : null,
        items: cart.map(item => ({
          product_id: item.product_id,
          name: item.product.name,
          price: item.product.price,
          quantity: item.quantity,
          image: item.product.images[0]
        })),
        total_amount: finalTotal,
        shipping_address: shippingAddress,
        billing_address: isSurpriseGift ? billingAddress : null,
        payment_method: paymentMethod,
        gift_wrap: giftWrap,
        gift_message: giftWrap ? giftMessage : null,
        is_surprise_gift: isSurpriseGift,
        coupon_code: appliedCoupon?.code || null,
        discount_amount: discount,
      };

      const response = await api.post('/orders', orderData);
      
      if (paymentMethod === 'cod') {
        // Increment coupon usage
        if (appliedCoupon) {
          try { await api.post('/coupons/increment', { code: appliedCoupon.code }); } catch {}
        }
        toast.success('Order placed! Pay on delivery.');
        await clearCart();
        navigate('/order-success/' + response.data.order.order_id);
      } else {
        // Open Razorpay checkout
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          toast.error('Payment gateway could not be loaded. Please try again.');
          setLoading(false);
          return;
        }

        const razorpayOrder = response.data.razorpay_order;
        const orderId = response.data.order.order_id;

        const options = {
          key: RAZORPAY_KEY,
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency || 'INR',
          name: 'Vastram',
          description: `Order #${orderId.substring(0, 8).toUpperCase()}`,
          order_id: razorpayOrder.id,
          handler: async (paymentResponse) => {
            // Verify payment on backend
            try {
              await api.post(`/orders/${orderId}/verify-payment`, {
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature
              });
              if (appliedCoupon) {
                try { await api.post('/coupons/increment', { code: appliedCoupon.code }); } catch {}
              }
              toast.success('Payment successful! Order confirmed.');
              await clearCart();
              navigate('/order-success/' + orderId);
            } catch (err) {
              toast.error('Payment verification failed. Contact support.');
            }
          },
          prefill: {
            name: user?.name || guestForm.name || '',
            email: user?.email || guestForm.email || '',
            contact: selectedAddress?.phone || guestForm.phone || '',
          },
          theme: { color: '#2D4A3E' },
          modal: {
            ondismiss: async () => {
              try { await api.post(`/orders/${orderId}/payment-failed`); } catch {}
              toast.error('Payment cancelled. Order not placed.');
              setLoading(false);
            }
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', async (resp) => {
          try { await api.post(`/orders/${orderId}/payment-failed`); } catch {}
          toast.error(resp.error?.description || 'Payment failed. Order not placed.');
          setLoading(false);
        });
        rzp.open();
        return; // Don't setLoading(false) yet — Razorpay modal is open
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  if (cartLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="checkout-loading">
        <p className="text-forest/60">Loading checkout...</p>
      </div>
    );
  }

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-heading text-4xl text-forest mb-8 tracking-tight" data-testid="checkout-heading">Checkout</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Delivery Address */}
          <div className="bg-neutral-warm p-6 rounded-sm">
            <h2 className="text-sm uppercase tracking-[0.2em] text-forest mb-4">Delivery Address</h2>
            {user ? (
              <AddressManager selectable onSelect={setSelectedAddress} selectedId={selectedAddress?.address_id} />
            ) : (
              <div className="space-y-4">
                {GOOGLE_MAPS_KEY && (
                  <div>
                    {!showGuestMap ? (
                      <button type="button" onClick={() => setShowGuestMap(true)}
                        className="flex items-center space-x-2 text-sm text-forest hover:text-gold transition-colors mb-2"
                        data-testid="guest-show-map-button">
                        <MapPin className="w-4 h-4" /><span>Pick location on map</span>
                      </button>
                    ) : (
                      <MapPicker onLocationSelect={(loc) => {
                        setGuestForm(prev => ({ ...prev, address: loc.address || prev.address, city: loc.city || prev.city, state: loc.state || prev.state, pincode: loc.pincode || prev.pincode }));
                      }} onClose={() => setShowGuestMap(false)} />
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="text" placeholder="Full Name *" value={guestForm.name} onChange={(e) => setGuestForm({ ...guestForm, name: e.target.value })}
                    className="px-4 py-3 bg-ivory border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest" data-testid="checkout-name-input" />
                  <input type="email" placeholder="Email *" value={guestForm.email} onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
                    className="px-4 py-3 bg-ivory border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest" data-testid="checkout-email-input" />
                  <input type="tel" placeholder="Phone *" value={guestForm.phone} onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })}
                    className="px-4 py-3 bg-ivory border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest" data-testid="checkout-phone-input" />
                </div>
                <input type="text" placeholder="Address *" value={guestForm.address} onChange={(e) => setGuestForm({ ...guestForm, address: e.target.value })}
                  className="w-full px-4 py-3 bg-ivory border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest" data-testid="checkout-address-input" />
                <div className="grid grid-cols-3 gap-4">
                  <input type="text" placeholder="City *" value={guestForm.city} onChange={(e) => setGuestForm({ ...guestForm, city: e.target.value })}
                    className="px-4 py-3 bg-ivory border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest" data-testid="checkout-city-input" />
                  <input type="text" placeholder="State *" value={guestForm.state} onChange={(e) => setGuestForm({ ...guestForm, state: e.target.value })}
                    className="px-4 py-3 bg-ivory border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest" data-testid="checkout-state-input" />
                  <input type="text" placeholder="PIN Code *" value={guestForm.pincode} onChange={(e) => setGuestForm({ ...guestForm, pincode: e.target.value })}
                    className="px-4 py-3 bg-ivory border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest" data-testid="checkout-pincode-input" />
                </div>
              </div>
            )}
          </div>

          {/* Payment Method */}
          <div className="bg-neutral-warm p-6 rounded-sm">
            <h2 className="text-sm uppercase tracking-[0.2em] text-forest mb-4">Payment Method</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button type="button" onClick={() => setPaymentMethod('online')}
                className={`flex items-center space-x-3 p-4 rounded-sm border-2 transition-all ${paymentMethod === 'online' ? 'border-forest bg-forest text-ivory shadow-md' : 'border-forest/15 bg-ivory hover:border-forest/30'}`}
                data-testid="payment-online-option">
                <CreditCard className={`w-6 h-6 ${paymentMethod === 'online' ? 'text-gold' : 'text-muted-foreground'}`} />
                <div className="text-left">
                  <p className={`text-sm font-medium ${paymentMethod === 'online' ? 'text-ivory' : 'text-forest'}`}>Pay Online</p>
                  <p className={`text-xs ${paymentMethod === 'online' ? 'text-ivory/70' : 'text-muted-foreground'}`}>Card, UPI, Net Banking</p>
                </div>
              </button>
              <button type="button" onClick={() => setPaymentMethod('cod')}
                className={`flex items-center space-x-3 p-4 rounded-sm border-2 transition-all ${paymentMethod === 'cod' ? 'border-forest bg-forest text-ivory shadow-md' : 'border-forest/15 bg-ivory hover:border-forest/30'}`}
                data-testid="payment-cod-option">
                <Banknote className={`w-6 h-6 ${paymentMethod === 'cod' ? 'text-gold' : 'text-muted-foreground'}`} />
                <div className="text-left">
                  <p className={`text-sm font-medium ${paymentMethod === 'cod' ? 'text-ivory' : 'text-forest'}`}>Cash on Delivery</p>
                  <p className={`text-xs ${paymentMethod === 'cod' ? 'text-ivory/70' : 'text-muted-foreground'}`}>Pay when you receive</p>
                </div>
              </button>
            </div>
          </div>

          {/* Gift Packaging */}
          <div className="bg-neutral-warm p-6 rounded-sm" data-testid="gift-wrap-section">
            <h2 className="text-sm uppercase tracking-[0.2em] text-forest mb-4">Gift Packaging</h2>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input type="checkbox" checked={giftWrap} onChange={(e) => { setGiftWrap(e.target.checked); if (!e.target.checked) setIsSurpriseGift(false); }}
                className="w-4 h-4 accent-forest" data-testid="gift-wrap-toggle" />
              <span className="text-sm text-forest">Add gift wrapping (+₹{GIFT_WRAP_COST})</span>
            </label>
            {giftWrap && (
              <div className="mt-4 space-y-4 pl-7">
                <textarea value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)}
                  placeholder="Add a personalised message for the recipient (optional)"
                  rows={2} className="w-full px-4 py-3 bg-ivory border border-forest/20 text-sm focus:outline-none focus:border-gold resize-none"
                  data-testid="gift-message-input" />
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input type="checkbox" checked={isSurpriseGift} onChange={(e) => setIsSurpriseGift(e.target.checked)}
                    className="w-4 h-4 accent-forest" data-testid="surprise-gift-toggle" />
                  <span className="text-sm text-forest">This is a surprise gift (hide my billing info from the package)</span>
                </label>
                {isSurpriseGift && (
                  <div className="space-y-3 border-l-2 border-gold/30 pl-4">
                    <p className="text-xs text-muted-foreground">Your billing details (won't appear on the package):</p>
                    <div className="grid grid-cols-2 gap-3">
                      <input type="text" placeholder="Your Name" value={billingAddress.name} onChange={(e) => setBillingAddress(p => ({...p, name: e.target.value}))}
                        className="px-3 py-2 bg-ivory border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="billing-name" />
                      <input type="tel" placeholder="Your Phone" value={billingAddress.phone} onChange={(e) => setBillingAddress(p => ({...p, phone: e.target.value}))}
                        className="px-3 py-2 bg-ivory border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="billing-phone" />
                    </div>
                    <input type="text" placeholder="Your Address" value={billingAddress.address} onChange={(e) => setBillingAddress(p => ({...p, address: e.target.value}))}
                      className="w-full px-3 py-2 bg-ivory border border-forest/20 text-sm focus:outline-none focus:border-gold" data-testid="billing-address" />
                    <div className="grid grid-cols-3 gap-3">
                      <input type="text" placeholder="City" value={billingAddress.city} onChange={(e) => setBillingAddress(p => ({...p, city: e.target.value}))} className="px-3 py-2 bg-ivory border border-forest/20 text-sm focus:outline-none focus:border-gold" />
                      <input type="text" placeholder="State" value={billingAddress.state} onChange={(e) => setBillingAddress(p => ({...p, state: e.target.value}))} className="px-3 py-2 bg-ivory border border-forest/20 text-sm focus:outline-none focus:border-gold" />
                      <input type="text" placeholder="PIN" value={billingAddress.pincode} onChange={(e) => setBillingAddress(p => ({...p, pincode: e.target.value}))} className="px-3 py-2 bg-ivory border border-forest/20 text-sm focus:outline-none focus:border-gold" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Coupon / Offers */}
          <div className="bg-neutral-warm p-6 rounded-sm">
            <h2 className="text-sm uppercase tracking-[0.2em] text-forest mb-4">Discount Coupon</h2>
            {appliedCoupon ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 px-4 py-3 rounded-sm" data-testid="applied-coupon">
                <div className="flex items-center space-x-2">
                  <Tag className="w-4 h-4 text-green-700" />
                  <span className="text-sm text-green-800 font-medium">{appliedCoupon.code}</span>
                  <span className="text-sm text-green-700">— You save ₹{appliedCoupon.discount.toLocaleString()}</span>
                </div>
                <button type="button" onClick={removeCoupon} className="p-1 text-green-600 hover:text-red-500" data-testid="remove-coupon-button"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex space-x-2">
                  <input type="text" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    className="flex-1 px-4 py-3 bg-ivory border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest text-sm uppercase tracking-wider"
                    data-testid="coupon-code-input" />
                  <button type="button" onClick={() => handleApplyCoupon()} disabled={applyingCoupon || !couponCode.trim()}
                    className="bg-forest text-ivory px-6 py-3 rounded-none text-sm uppercase tracking-[0.1em] hover:bg-gold hover:text-forest transition-colors disabled:opacity-50"
                    data-testid="apply-coupon-button">
                    {applyingCoupon ? '...' : 'Apply'}
                  </button>
                </div>
                {availableCoupons.length > 0 && (
                  <div>
                    <button type="button" onClick={() => setShowOffers(!showOffers)}
                      className="flex items-center space-x-1 text-sm text-forest hover:text-gold transition-colors" data-testid="view-offers-button">
                      <Tag className="w-3.5 h-3.5" />
                      <span>View available offers ({availableCoupons.length})</span>
                      {showOffers ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                    {showOffers && (
                      <div className="mt-3 space-y-2" data-testid="available-coupons-list">
                        {availableCoupons.map((coupon) => (
                          <div key={coupon.coupon_id} className="flex items-center justify-between bg-ivory p-3 rounded-sm border border-gold/20" data-testid="coupon-offer-card">
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-mono font-medium text-forest bg-gold/20 px-2 py-0.5 rounded-sm">{coupon.code}</span>
                                <span className="text-sm text-forest">{coupon.discount_type === 'percentage' ? `${coupon.discount_value}% off` : `₹${coupon.discount_value} off`}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {coupon.min_order > 0 ? `Min. order ₹${coupon.min_order.toLocaleString()}` : 'No minimum'}
                                {coupon.max_discount ? ` | Max ₹${coupon.max_discount.toLocaleString()} off` : ''}
                              </p>
                            </div>
                            <button type="button" onClick={() => handleApplyCoupon(coupon.code)}
                              className="text-xs text-forest font-medium uppercase underline hover:text-gold transition-colors" data-testid="apply-offer-button">Apply</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="bg-neutral-warm p-6 rounded-sm">
            <h2 className="text-sm uppercase tracking-[0.2em] text-forest mb-4">Order Summary</h2>
            <div className="space-y-3 mb-4">
              {cart.map((item) => (
                <div key={item.product_id} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{item.product.name} x {item.quantity}</span>
                  <span className="text-forest">₹{(item.product.price * item.quantity).toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-gold pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-forest">₹{cartTotal.toLocaleString()}</span>
              </div>
              {appliedCoupon && (
                <div className="flex justify-between text-sm text-green-700">
                  <span>Discount ({appliedCoupon.code})</span>
                  <span>- ₹{discount.toLocaleString()}</span>
                </div>
              )}
              {giftWrap && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Gift Packaging</span>
                  <span className="text-forest">+ ₹{GIFT_WRAP_COST}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">GST (included)</span>
                <span className="text-forest text-xs">₹{gstData.totalGST.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Delivery</span>
                <span className="text-green-600 font-medium">₹0 (Free)</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gold/30">
                <span className="font-heading text-xl text-forest">Total</span>
                <span className="font-heading text-xl text-forest" data-testid="checkout-total">₹{finalTotal.toLocaleString()}</span>
              </div>
              <p className="text-xs text-muted-foreground text-right">Including all taxes</p>
              {paymentMethod === 'cod' && <p className="text-xs text-gold text-right">Cash on Delivery</p>}
            </div>
          </div>

          <button type="submit" disabled={loading}
            className="w-full bg-forest text-ivory px-8 py-4 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em] disabled:opacity-50"
            data-testid="place-order-button">
            {loading ? 'Processing...' : paymentMethod === 'cod' ? `Place Order (COD) — ₹${finalTotal.toLocaleString()}` : `Place Order & Pay — ₹${finalTotal.toLocaleString()}`}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
